import { DataTableColumnHeader } from "@/components/ui";
import { formatTableColumnLabel } from "@/components/ui/shadcn/DataTable/formatTableColumnLabel";
import { type Contact } from "@/lib/types";
import { formatDate, formatLocalOrIntlPhoneNumber } from "@/lib/utils/format";
import { MultiValueCell, renderCheckedCell } from "@/lib/utils/tableUtils";
import { type ColumnDef } from "@tanstack/react-table";
import { startCase } from "lodash";

export type ContactsTableColumnHeadersMode = "dataTable" | "plain";

const normalizeStringArray = (values?: unknown) => {
  if (!Array.isArray(values)) return [];
  return values
    .map((value) => (typeof value === "string" ? value.trim() : ""))
    .filter((value): value is string => Boolean(value));
};

const normalizeAddressString = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) {
    return "";
  }

  if (trimmed.includes(",")) {
    return trimmed
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean)
      .join(", ");
  }

  const usAddressWithZipMatch = trimmed.match(
    /^(?<street>.+?)\s+(?<city>[A-Za-z .'-]+)\s+(?<state>[A-Z]{2})\s+(?<zip>\d{5}(?:-\d{4})?)(?:\s+(?<country>.+))?$/,
  );
  if (usAddressWithZipMatch?.groups) {
    const { street, city, state, zip, country } = usAddressWithZipMatch.groups;
    return `${street.trim()}, ${city.trim()}, ${state.trim()} ${zip.trim()}${country ? `, ${country.trim()}` : ""}`;
  }

  const usAddressMatch = trimmed.match(
    /^(?<street>.+?)\s+(?<city>[A-Za-z .'-]+)\s+(?<state>[A-Z]{2})(?:\s+(?<country>.+))?$/,
  );
  if (usAddressMatch?.groups) {
    const { street, city, state, country } = usAddressMatch.groups;
    return `${street.trim()}, ${city.trim()}, ${state.trim()}${country ? `, ${country.trim()}` : ""}`;
  }

  return trimmed;
};

export const getContactsTableColumns = (options?: {
  columnHeaders?: ContactsTableColumnHeadersMode;
}) => {
  const columnHeaders = options?.columnHeaders ?? "dataTable";
  const contactsTableColumns: ColumnDef<Contact>[] = [
    {
      accessorFn: (row) => row.dateCreated,
      id: "Date Created",
      sortingFn: "customDateTime",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: (row) => <div>{formatDate(row.getValue() as string)}</div>,
    },
    {
      accessorFn: (row) => row.dateModified,
      id: "Date Modified",
      sortingFn: "customDateTime",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: (row) => <div>{formatDate(row.getValue() as string)}</div>,
    },
    {
      accessorFn: (row) =>
        [
          row.individualFirstName,
          row.individualMiddleName,
          row.individualLastName,
          row.organizationName,
          row.groupName,
        ]
          .filter(Boolean)
          .join(" "),
      id: "Name",
      size: 300,
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      enableHiding: false,
    },
    {
      accessorFn: (row) => startCase(row.type ?? ""),
      id: "Type",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
    },
    {
      accessorFn: (row) =>
        normalizeStringArray(
          row.type === "individual"
            ? row.individualSubTypes
            : row.organizationSubTypes,
        ),
      id: "Sub Type",
      sortUndefined: -1,
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: (row) => {
        const subtypes = row.getValue() as string[] | [];
        return <MultiValueCell value={subtypes} />;
      },
    },
    {
      accessorFn: (row) =>
        row.addresses.map((address) =>
          [
            address.street1,
            address.street2,
            address.city,
            address.state,
            address.zipCode,
            address.country,
          ]
            .filter(Boolean)
            .join(", "),
        ),
      id: "Full Address",
      size: 425,
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: (row) => {
        const addresses = row.getValue() as string[] | [];
        return (
          <MultiValueCell
            value={addresses.map((address) => normalizeAddressString(address))}
            includeFirstItemInTooltip
          />
        );
      },
    },
    {
      accessorFn: (row) =>
        row.emails.map((email) => email.address).filter(Boolean),
      id: "Email",
      size: 260,
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: (row) => {
        const emails = row.getValue() as string[] | [];
        return <MultiValueCell value={emails} includeFirstItemInTooltip />;
      },
    },
    {
      accessorFn: (row) =>
        row.phones
          .map((phone) => formatLocalOrIntlPhoneNumber(phone))
          .filter(Boolean),
      id: "Phone",
      size: 200,
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: (row) => {
        const phonesString = row.getValue() as string[] | [];
        return (
          <MultiValueCell value={phonesString} includeFirstItemInTooltip />
        );
      },
    },
    {
      accessorFn: (row) =>
        row.faxes
          .map((fax) => formatLocalOrIntlPhoneNumber(fax))
          .filter(Boolean),
      id: "Fax",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: (row) => {
        const faxesString = row.getValue() as string[] | [];
        return <MultiValueCell value={faxesString} includeFirstItemInTooltip />;
      },
    },
    {
      accessorFn: (row) =>
        row.websites.map((website) => website.address).filter(Boolean),
      id: "Website",
      size: 260,
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: (row) => {
        const websites = row.getValue() as string[] | [];
        return <MultiValueCell value={websites} includeFirstItemInTooltip />;
      },
    },
    {
      accessorFn: (row) =>
        row.tagConnections.map((connection) => connection.label),
      id: "Tags",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: (row) => {
        const tags = row.getValue() as string[] | [];
        return <MultiValueCell value={tags} includeFirstItemInTooltip />;
      },
    },
    {
      accessorFn: (row) => row.individualDateOfBirth,
      id: "Birthday",
      sortingFn: "customDateTime",
      sortUndefined: -1,
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: (row) => (
        <div>{formatDate(row.getValue() as string | undefined) || "-"}</div>
      ),
    },
    {
      accessorFn: (row) =>
        normalizeStringArray([
          row.individualPrimaryLanguage,
          row.individualSecondaryLanguage,
        ]),
      id: "Language",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: (row) => {
        const languages = row.getValue() as string[];
        return <MultiValueCell value={languages} includeFirstItemInTooltip />;
      },
    },
    {
      accessorFn: (row) => normalizeStringArray(row.individualRace),
      id: "Race",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: (row) => {
        const races = row.getValue() as string[] | [];
        return <MultiValueCell value={races} includeFirstItemInTooltip />;
      },
    },
    {
      accessorFn: (row) => row.individualGender,
      id: "Gender",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: (row) => <div>{(row.getValue() as string | undefined) ?? "-"}</div>,
    },
    {
      accessorFn: (row) => row.individualDeceased,
      id: "Deceased",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: (row) => renderCheckedCell({ props: row }),
    },
  ];

  if (columnHeaders === "plain") {
    return contactsTableColumns.map((col) => ({
      ...col,
      header: formatTableColumnLabel(col.id as string),
    }));
  }

  return contactsTableColumns;
};
